import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  forwardRef,
  inject,
  input,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { EditorComponent, EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { Subscription } from 'rxjs';

const CONTENT_STYLES = `
  body { font-family: Arial, sans-serif; font-size: 14px; color: #1e293b; line-height: 1.5; margin: 8px; }
  p, h1, h2, h3, h4, h5, h6, li, blockquote, pre { margin: 0; padding: 0; line-height: 1.5; }
  ul, ol { margin: 0; padding-left: 1.5em; line-height: 1.5; }
  ul ul, ol ol, ul ol, ol ul { padding-left: 1.25em; }
  .token-badge {
    display: inline-block; padding: 0 4px 0 6px; border-radius: 3px;
    background: #eff6ff; border: 1px solid #bfdbfe; color: #1e40af;
    font-size: 10px; font-weight: 600; font-family: monospace;
    cursor: default; user-select: none; white-space: nowrap;
    vertical-align: middle; line-height: 1.8;
  }
  .token-remove {
    display: inline-flex; align-items: center; justify-content: center;
    margin-left: 4px; width: 13px; height: 13px; border-radius: 50%;
    background: rgba(30,64,175,0.15); color: #1e40af;
    font-size: 10px; cursor: pointer; vertical-align: middle;
    font-style: normal; font-weight: 700; line-height: 1; text-decoration: none;
  }
  .token-remove:hover { background: rgba(30,64,175,0.35); }
  table { border-collapse: collapse; width: 100%; font-size: 0.875em; margin: 0.75em 0; }
  table td, table th { border: 1px solid #cbd5e1; padding: 7px 11px; min-width: 3em; vertical-align: top; line-height: 1.5; }
  table thead th { background: #1e293b; color: #f8fafc; font-weight: 600; text-align: left; letter-spacing: 0.02em; }
  table tbody th { background: #f1f5f9; font-weight: 600; color: #1e293b; text-align: left; }
  table tbody tr:nth-child(even) td { background: #f8fafc; }
`;

const FULL_TOOLBAR =
  'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | bullist numlist outdent indent | forecolor backcolor | alignleft aligncenter alignright alignjustify | removeformat | table link image | code fullscreen';

const FULL_PLUGINS =
  'advlist autolink lists link image charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime media table wordcount';

const EXTENDED_VALID_ELEMENTS =
  'span[class|data-token|contenteditable|style],em[class|title]';

// Handles the × click inside badges — module-level so config objects are stable references.
const badgeRemoveSetup = (editor: any): void => {
  editor.on('click', (e: any) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('token-remove')) {
      const badge = target.closest('[data-token]');
      if (badge) {
        editor.dom.remove(badge);
        editor.fire('change');
      }
    }
  });
};

const BASE_CONFIG = {
  base_url: '/tinymce',
  suffix: '.min',
  license_key: 'gpl',
  promotion: false,
  branding: false,
  height: '100%' as const,
  menubar: false,
  resize: false,
  content_style: CONTENT_STYLES,
  extended_valid_elements: EXTENDED_VALID_ELEMENTS,
  entity_encoding: 'raw' as const,
  setup: badgeRemoveSetup,
};

const FULL_CONFIG = {
  ...BASE_CONFIG,
  toolbar_mode: 'wrap' as const,
  toolbar: FULL_TOOLBAR,
  plugins: FULL_PLUGINS,
};

const SIMPLE_CONFIG = {
  ...BASE_CONFIG,
  toolbar: false as const,
  plugins: '',
};

/** Wrap a token into a badge span with a removable × button. */
export function tokenBadgeHtml(key: string, label: string): string {
  return `<span class="token-badge" data-token="${key}" contenteditable="false">${label}<em class="token-remove" title="Remove">×</em></span>​`;
}

@Component({
  selector: 'app-tinymce-editor',
  standalone: true,
  imports: [CommonModule, EditorModule, ReactiveFormsModule],
  template: `
    @if (isBrowser) {
      <editor [init]="activeEditorConfig" [formControl]="innerControl"></editor>
    }
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        width: 100%;
      }
      /* Make the Angular EditorComponent host fill :host */
      :host ::ng-deep editor {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
      }
      /* TinyMCE root fills the editor component;
         height: 100% set by TinyMCE init is then relative to this resolved height */
      :host ::ng-deep .tox-tinymce {
        flex: 1 !important;
        min-height: 0 !important;
        border-radius: 8px !important;
      }
      /* Force the iframe to fill its edit-area (overrides any JS-set px height) */
      :host ::ng-deep .tox-edit-area__iframe {
        height: 100% !important;
      }
      :host ::ng-deep .tox-statusbar { display: none !important; }
      /* Toolbar wraps its buttons onto new lines instead of scrolling
         horizontally when the editor is too narrow to fit them on one row. */
      :host ::ng-deep .tox-tinymce,
      :host ::ng-deep .tox .tox-editor-header {
        max-width: 100% !important;
      }
      :host ::ng-deep .tox .tox-toolbar,
      :host ::ng-deep .tox .tox-toolbar__primary,
      :host ::ng-deep .tox .tox-toolbar__overflow {
        flex-wrap: wrap !important;
        overflow-x: hidden !important;
      }
    `,
  ],
  providers: [
    { provide: TINYMCE_SCRIPT_SRC, useValue: '/tinymce/tinymce.min.js' },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TinyMCEEditorComponent),
      multi: true,
    },
  ],
})
export class TinyMCEEditorComponent implements ControlValueAccessor, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  protected readonly isBrowser = isPlatformBrowser(this.platformId);

  /** 'full' shows the complete toolbar; 'simple' hides it (text-only editing). */
  readonly mode = input<'full' | 'simple'>('full');

  @ViewChild(EditorComponent) private editorComponent?: EditorComponent;

  protected readonly innerControl = new FormControl('');

  protected get activeEditorConfig() {
    return this.mode() === 'simple' ? SIMPLE_CONFIG : FULL_CONFIG;
  }

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private readonly sub = new Subscription();

  constructor() {
    this.sub.add(
      this.innerControl.valueChanges.subscribe((value) => {
        this.onChange(value ?? '');
        this.onTouched();
      }),
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  insertToken(key: string, label: string): void {
    const editor = this.editorComponent?.editor;
    if (!editor) return;
    editor.insertContent(tokenBadgeHtml(key, label));
  }

  // ── ControlValueAccessor ──────────────────────────────────────

  writeValue(value: string): void {
    this.innerControl.setValue(value ?? '', { emitEvent: false });
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.innerControl.disable({ emitEvent: false });
    } else {
      this.innerControl.enable({ emitEvent: false });
    }
  }
}
